import { useSubscription } from '@/composables/subscription'
import routesLimited from '@/constants/routesLimited'
import { isCypress } from '@/helpers/isCypress'
import { buildingService } from '@/services/api'
import { useBuildingStore } from '@/stores/modules/building'
import { useSettingStore } from '@/stores/modules/setting'
import { useLiaStore } from '@/stores/system/lia'
import { useTourStore } from '@/stores/system/tour'
import { useUserStore } from '@/stores/system/user'
import { storeToRefs } from 'pinia'
import { driver, type Driver, type DriveStep } from 'driver.js'
import { router } from '@/plugins/router'

type GateContext = {
	checkIfSubscribedTo: ReturnType<typeof useSubscription>['checkIfSubscribedTo']
	settings: ReturnType<typeof useSettingStore>['getData'] | undefined
}

type TourStepDefinition = {
	id: string
	title?: string
	text: string
	/** CSS selectors tried in order; first match wins. Omit for centered modals. */
	selectors?: string[]
	placement?: 'top' | 'bottom' | 'left' | 'right' | 'over'
	/**
	 * Optional gating predicate; step is dropped if it returns false.
	 * May be sync or async — async lets steps probe the API for actual
	 * data availability (e.g. buildingMeters only shows if the user's
	 * first building actually has meters).
	 */
	gate?: (ctx: GateContext) => boolean | Promise<boolean>
	/** Route name to push before this step is shown. */
	route?: string
	/** Extra setup before show (e.g. open drawer, scroll). */
	prepare?: () => Promise<void> | void
	/** Anchor lives in Drawer's #append slot; expand the rail first. */
	needsExpandedDrawer?: boolean
	/**
	 * Show-time guard, evaluated AFTER prepare/navigation. Return false to skip
	 * the step entirely (instead of rendering it on the wrong page). Used by the
	 * building steps to confirm we actually landed on `building/:id` — if the
	 * navigation never happened (no building, blocked push), the step is dropped
	 * rather than shown as a centered popover with the wrong context.
	 */
	verifyOnShow?: () => boolean
}

const PROFILE_INTRO_SELECTORS = ['[data-test="profile-info-card"]']

/**
 * True when the SPA is actually on a building detail route (`building/:id`).
 * The building tour steps use this as a `verifyOnShow` guard so they are skipped
 * — rather than rendered as a centered popover on the wrong page — when the
 * pre-step navigation to the building never happened (no building, blocked push).
 */
function isOnBuildingRoute(): boolean {
	const route = router.currentRoute.value
	return route.name === 'building' && !!route.params?.id
}

/**
 * Tour step registry. Steps without a matching anchor or whose gate fails are
 * silently skipped when start() is called.
 */
function buildStepRegistry(): TourStepDefinition[] {
	return [
		{
			id: 'welcome',
			title: 'Willkommen im Portal',
			text:
				'Wir zeigen Ihnen in wenigen Schritten die wichtigsten Funktionen.' +
				' Sie können die Tour jederzeit über das ✕ oben rechts beenden.',
		},
		{
			id: 'toolbar',
			title: 'Navigation',
			text:
				'Über diesen Bereich können Sie die seitliche Navigation ein- und ausklappen.',
			selectors: ['[data-v-step="toolbar"] .v-toolbar__prepend', '[data-v-step="toolbar"]'],
			placement: 'bottom',
		},
		{
			id: 'reportButton',
			title: 'Anliegen melden',
			text:
				'Sie haben eine Frage oder möchten einen Schaden melden? Hier senden Sie Ihr Anliegen direkt an Ihre Verwaltung.',
			selectors: ['[data-tour="reportButton"]', '[data-test="appBar__report-btn"]'],
			placement: 'bottom',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_report &&
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed,
		},
		{
			id: 'notifyButton',
			title: 'Benachrichtigungen',
			text:
				'Hier finden Sie Mitteilungen Ihrer Verwaltung, neue Dokumente und Aktualisierungen.',
			selectors: ['[data-tour="notifyButton"]', '[data-v-step="notifyButton"]'],
			placement: 'bottom',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.notifications).allowed,
		},
		{
			id: 'notes',
			title: 'Aktuelles aus Ihrer Liegenschaft',
			text:
				'Allgemeine Informationen und liegenschaftsbezogene Neuigkeiten erscheinen hier.',
			selectors: ['[data-v-step="notes"]', '[data-tour="notes"]'],
			placement: 'top',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.notes).allowed,
		},
		{
			id: 'navDocuments',
			title: 'Dokumente',
			text:
				'Unter Dokumente sehen Sie alle für Sie freigegebenen Unterlagen Ihrer Verwaltung.',
			selectors: ['[data-v-step="navDocuments_group"]', '[data-tour="navDocuments"]'],
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.documents).allowed,
		},
		{
			id: 'navReviewDocuments',
			title: 'Belegprüfung',
			text:
				'Hier können Sie die zur Belegprüfung freigegebenen Unterlagen einsehen und – sofern Sie dazu berechtigt sind – im Rahmen der Belegprüfung freigeben.',
			selectors: ['[data-v-step="navReviewDocuments"]', '[data-tour="navReviewDocuments"]'],
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.review_documents).allowed,
		},
		{
			id: 'navPendingRequests',
			title: 'Offene Anfragen',
			text:
				'Stammdatenänderungen und Zählerstands-Freigaben Ihrer Verwaltung finden Sie hier.',
			selectors: ['[data-v-step="navPendingRequests"]', '[data-tour="navPendingRequests"]'],
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed,
		},
		{
			id: 'navFaqs',
			title: 'Hilfe & FAQ',
			text:
				'Im FAQ-Bereich finden Sie Antworten auf häufige Fragen rund um das Portal und die Zusammenarbeit mit Ihrer Verwaltung.',
			selectors: ['[data-v-step="navFaqs"]', '[data-tour="navFaqs"]'],
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.faqs).allowed,
		},
		{
			id: 'themeLocaleProfile',
			title: 'Sprache, Design & Profil-Menü',
			text:
				'Hinter Ihrem Profil-Symbol oben rechts können Sie zusätzlich die Sprache umstellen, das Design (hell/dunkel) wechseln und sich abmelden.',
			selectors: ['[data-v-step="profileButton"]'],
			placement: 'bottom',
		},
		{
			id: 'buildingDetail',
			title: 'Ihre Liegenschaft im Detail',
			text:
				'Hier sehen Sie alle Informationen rund um Ihre Liegenschaft und die zugehörigen Einheiten – je nach Freigabe Ihrer Verwaltung z. B. Dokumente, Notizen, Personen, Zähler, Buchhaltung oder Belege.',
			selectors: ['[data-test="buildings-sidenav"]', '[data-test="buildings"]'],
			placement: 'right',
			verifyOnShow: isOnBuildingRoute,
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.buildings).allowed,
			prepare: async () => {
				const store = useBuildingStore()
				let buildings: { id: number }[] | undefined =
					(store.data as { id: number }[] | undefined) ??
					(store.dataInit as { id: number }[] | undefined)
				if (!buildings || buildings.length === 0) {
					try {
						const loaded = await store.load()
						if (Array.isArray(loaded)) {
							buildings = loaded as { id: number }[]
						} else if (store.dataInit) {
							buildings = store.dataInit as { id: number }[]
						}
					} catch {
						/* ignore – step falls back to a centered tooltip */
					}
				}
				const buildingId = buildings?.[0]?.id
				const current = router.currentRoute.value
				if (
					buildingId &&
					(current.name !== 'building' ||
						Number(current.params?.id) !== buildingId)
				) {
					try {
						await router.push({
							name: 'building',
							params: { id: buildingId },
						})
					} catch {
						/* ignore */
					}
				}
			},
		},
		{
			id: 'buildingMeters',
			title: 'Zählerstände erfassen',
			text:
				'Auf der Registerkarte „Zähler" können Sie regelmäßig Ihre Zählerstände – Wasser, Strom, Wärme – erfassen und an die Verwaltung übermitteln.',
			selectors: [
				'[data-test="buildings-sidenav"] [data-test="buildingMeters-tab"]',
			],
			placement: 'bottom',
			verifyOnShow: isOnBuildingRoute,
			gate: async ({ checkIfSubscribedTo, settings }) => {
				if (settings?.hide_meter_reading) return false
				if (!checkIfSubscribedTo(routesLimited.subModules.meters).allowed) {
					return false
				}
				const store = useBuildingStore()
				let buildings: { id: number }[] | undefined =
					(store.data as { id: number }[] | undefined) ??
					(store.dataInit as { id: number }[] | undefined)
				if (!buildings || buildings.length === 0) {
					try {
						const loaded = await store.load()
						if (Array.isArray(loaded)) {
							buildings = loaded as { id: number }[]
						} else if (store.dataInit) {
							buildings = store.dataInit as { id: number }[]
						}
					} catch {
						return false
					}
				}
				const buildingId = buildings?.[0]?.id
				if (!buildingId) return false
				try {
					const { data } = await buildingService.itemModuleGet<
						Api.Collection<{ id: number }>
					>(buildingId, 'meters')
					return Array.isArray(data?.data) && data.data.length > 0
				} catch {
					return false
				}
			},
			prepare: async () => {
				// buildingDetail already navigated to /buildings/:id — stay there.
				// Just give BuildingNav.fillTabs() a tick to populate the tabs.
				await new Promise((r) => setTimeout(r, 100))
			},
		},
		{
			id: 'profileIntro',
			title: 'Ihr Profil',
			text:
				'In Ihrem Profil sehen Sie, welche Daten Ihre Verwaltung über Sie gespeichert hat: persönliche Stammdaten, Liegenschaften und Ihre Meldungen.',
			selectors: PROFILE_INTRO_SELECTORS,
			placement: 'bottom',
			route: 'profile',
		},
		{
			id: 'profileReportsTab',
			title: 'Ihre Meldungen im Überblick',
			text:
				'Auf der Registerkarte „Meldungen" finden Sie alle Anliegen, die Sie bereits an Ihre Verwaltung gesendet haben – inklusive aktuellem Bearbeitungsstatus.',
			selectors: ['[data-test="reports-reports-datatable"]'],
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_report &&
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed,
			prepare: async () => {
				const tab = document.querySelector<HTMLElement>(
					'[data-test="profile-tabs"] [data-test="reports-tab"]'
				)
				if (tab && !tab.classList.contains('v-tab--selected')) {
					tab.click()
					await new Promise((r) => setTimeout(r, 200))
				}
			},
		},
		{
			id: 'profileChangePassword',
			title: 'Passwort ändern',
			text:
				'Hier können Sie jederzeit Ihr Passwort ändern. Wir empfehlen, das initiale Passwort beim ersten Login zu ersetzen.',
			selectors: ['[data-test="change-password-btn"]'],
			placement: 'top',
			route: 'profile',
		},
		{
			id: 'profileSettings',
			title: 'Benachrichtigungs-Einstellungen',
			text:
				'Über die Einstellungen entscheiden Sie selbst, zu welchen Ereignissen Sie per E-Mail informiert werden möchten – z. B. neue Dokumente, Notizen oder Meldungs-Updates.',
			selectors: ['[data-test="settings-btn"]'],
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo }) =>
				[
					routesLimited.subModules.notifications,
					routesLimited.subModules.notes,
				].every((sub) => checkIfSubscribedTo(sub).allowed),
		},
		{
			id: 'profilePersonUpdate',
			title: 'Stammdaten aktualisieren',
			text:
				'Über diesen Knopf können Sie eine Änderung Ihrer persönlichen Daten an die Verwaltung senden. Die Verwaltung prüft und übernimmt die Änderung.',
			selectors: ['[data-test="edit-profile-btn"]'],
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_update_requests &&
				checkIfSubscribedTo(
					routesLimited.subMethods.notification_person_update
				).allowed,
		},
		{
			id: 'propertyManagers',
			title: 'Ihre Verwaltung',
			text:
				'Hier sehen Sie Ihre Hausverwaltung mit Ansprechpartner und Adresse — immer griffbereit, wenn Sie Kontakt aufnehmen möchten.',
			selectors: ['[data-test="propertymanagers"]'],
			placement: 'top',
			route: 'profile',
		},
		{
			id: 'personDetail',
			title: 'Ihre persönlichen Daten',
			text:
				'Auf Ihrer Personenseite finden Sie alle direkt zugeordneten Informationen: persönliche Dokumente, E-Mail-Adressen, Telefonnummern sowie Ihre Rollen und Berechtigungen.',
			selectors: ['[data-test="person-container"]'],
			placement: 'bottom',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.persons).allowed &&
				(useUserStore().whoami?.persons?.length ?? 0) > 0,
			prepare: async () => {
				const personId = useUserStore().whoami?.persons?.[0]?.id
				const current = router.currentRoute.value
				if (
					personId &&
					(current.name !== 'person' ||
						Number(current.params?.id) !== personId)
				) {
					try {
						await router.push({
							name: 'person',
							params: { id: personId },
						})
					} catch {
						/* ignore */
					}
				}
			},
		},
		{
			id: 'privacyTos',
			title: 'Datenschutz & Nutzungsbedingungen',
			text:
				'Die aktuellen Datenschutz- und Nutzungsbedingungen können Sie hier jederzeit einsehen.',
			selectors: ['[data-v-step="navPrivacyTos"]', '[data-tour="navPrivacyTos"]'],
			placement: 'top',
			route: 'dashboard',
			needsExpandedDrawer: true,
		},
		{
			id: 'outro',
			title: 'Sie sind startklar',
			text:
				'Sie können diese Tour jederzeit über <strong>Profil → Einstellungen → „Einführungstour erneut starten"</strong> erneut starten.',
		},
	]
}

async function findFirstPresent(
	selectors: string[],
	timeoutMs = 2500
): Promise<{ element: HTMLElement; selector: string } | null> {
	const deadline = Date.now() + timeoutMs
	while (Date.now() < deadline) {
		for (const sel of selectors) {
			const el = document.querySelector<HTMLElement>(sel)
			if (el) return { element: el, selector: sel }
		}
		await new Promise((r) => setTimeout(r, 80))
	}
	return null
}

async function waitForGone(selector: string, timeoutMs = 1500): Promise<void> {
	const deadline = Date.now() + timeoutMs
	while (Date.now() < deadline) {
		if (!document.querySelector(selector)) return
		await new Promise((r) => setTimeout(r, 50))
	}
}

let currentDriver: Driver | null = null
let starting = false
let didComplete = false
// Run-id guards an in-flight start() against being cancelled by a stop()
// while it's still awaiting navigation/gates. Each start() bumps the id and
// captures it locally; after every await it re-checks vs the module-level
// id and bails if a stop()/restart() has happened in the meantime.
let runId = 0
// Set to true by stop() so the destroy() it triggers doesn't get recorded
// as a user-initiated skip (which would suppress the next auto-start).
let suppressPersist = false

export function useTour() {
	const userStore = useUserStore()
	const tourStore = useTourStore()
	const settingStore = useSettingStore()
	const { isAdmin, isTeam, whoami } = storeToRefs(userStore)
	const { person: liaPerson } = storeToRefs(useLiaStore())
	const { checkIfSubscribedTo } = useSubscription()

	async function ensureDrawerExpanded() {
		// We used to flip the `mini` cookie here, but @vueuse/useCookies
		// has per-instance change listeners — writes from useTour don't
		// reactively re-trigger Drawer.vue's `computed(() => cookies.get('mini'))`.
		// Instead, simulate a mouseenter on the v-navigation-drawer: Drawer.vue
		// sets `hover = true`, and Vuetify's `expand-on-hover` (configured as
		// `hover && drawer && mini`) expands the rail without any cookie write.
		const drawerEl = document.querySelector<HTMLElement>(
			'.v-navigation-drawer.nav'
		)
		if (!drawerEl) return
		const isMini = drawerEl.classList.contains('v-navigation-drawer--rail')
		if (!isMini) return
		drawerEl.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }))
		// Wait for v-navigation-drawer rail->expanded transition.
		await new Promise((r) => setTimeout(r, 350))
	}

	function canRun(): boolean {
		if (!whoami.value) return false
		if (isAdmin.value || isTeam.value) return false
		if (liaPerson.value) return false
		if (typeof window !== 'undefined' && window.innerWidth < 960) return false
		return true
	}

	/**
	 * Under Cypress the tour auto-start races every dashboard spec that runs
	 * as a non-team persona — the driver.js overlay covers the page mid-assert.
	 * Auto-start is therefore OFF in E2E by default; tour.cy.ts opts back in
	 * via the localStorage flag below. Explicit restarts (start({ force }))
	 * are unaffected.
	 */
	function autoStartSuppressedByCypress(): boolean {
		return (
			isCypress() &&
			window.localStorage.getItem('__cypress_tour_autostart__') !== '1'
		)
	}

	function shouldAutoStart(): boolean {
		if (autoStartSuppressedByCypress()) return false
		if (!canRun()) return false
		return tourStore.shouldAutoStart(whoami.value?.id)
	}

	async function buildSteps(): Promise<TourStepDefinition[]> {
		const ctx: GateContext = {
			checkIfSubscribedTo,
			settings: settingStore.getData,
		}
		const registry = buildStepRegistry()
		const checks = await Promise.all(
			registry.map(async (def): Promise<TourStepDefinition | null> => {
				if (def.gate) {
					const allowed = await def.gate(ctx)
					if (!allowed) return null
				}
				const navigatesLater = !!def.route || !!def.prepare
				if (def.selectors && !navigatesLater) {
					const hit = await findFirstPresent(def.selectors, 400)
					if (!hit) return null
				}
				return def
			})
		)
		return checks.filter((s): s is TourStepDefinition => s !== null)
	}

	/**
	 * Run a step's side-effects (route navigation, drawer expand, prepare
	 * hook, anchor wait). Driver.js has no per-step beforeShowPromise — we
	 * call this from the TourFooter button handlers BEFORE driver.moveNext().
	 */
	async function runStepPrepare(def: TourStepDefinition): Promise<boolean> {
		if (def.route && router.currentRoute.value.name !== def.route) {
			try {
				await router.push({ name: def.route })
			} catch {
				/* ignore */
			}
		}
		if (def.needsExpandedDrawer) await ensureDrawerExpanded()
		if (def.prepare) await def.prepare()
		// Show-time guard, evaluated after navigation/prepare. Report "not
		// showable" so the caller skips this step — instead of rendering a
		// popover with the wrong context — when either the verifyOnShow guard
		// fails (e.g. navigation never reached building/:id) or the step's
		// anchor never appears.
		if (def.verifyOnShow && !def.verifyOnShow()) return false
		if (def.selectors) {
			const hit = await findFirstPresent(def.selectors, 2500)
			if (!hit) return false
		}
		return true
	}

	async function start({ force = false }: { force?: boolean } = {}) {
		if (!canRun()) return
		if (starting || currentDriver?.isActive()) return
		if (!force && !shouldAutoStart()) return

		starting = true
		const myRunId = ++runId
		const cancelled = () => myRunId !== runId
		try {
			if (router.currentRoute.value.name !== 'dashboard') {
				try {
					await router.push({ name: 'dashboard' })
				} catch {
					/* ignore */
				}
				if (cancelled()) return
				await findFirstPresent(['[data-test="dashboard"]'], 3000)
				if (cancelled()) return
			}

			await waitForGone('.v-overlay--active', 1500)
			if (cancelled()) return

			const steps = await buildSteps()
			if (cancelled() || !canRun()) return
			if (steps.length === 0) return

			const userId = whoami.value?.id
			didComplete = false

			// Build driver.js step list. `element` is a function so the
			// anchor selector is resolved at show-time (after runStepPrepare).
			const driveSteps: DriveStep[] = steps.map((def) => ({
				element: def.selectors
					? () => {
							for (const sel of def.selectors!) {
								const el = document.querySelector(sel)
								if (el) return el
							}
							// driver.js requires an Element; fall back to body so
							// the popover renders centered.
							return document.body
						}
					: undefined,
				popover: {
					title: def.title,
					description: def.text,
					side: def.placement ?? 'bottom',
					align: 'center',
					// We render our own footer via Teleport — hide driver's
					// next/previous buttons but keep the close X.
					showButtons: ['close'],
				},
			}))

			currentDriver = driver({
				steps: driveSteps,
				animate: true,
				smoothScroll: true,
				allowClose: true,
				allowKeyboardControl: false, // TourFooter owns the keyboard handler
				stagePadding: 6,
				stageRadius: 8,
				overlayOpacity: 0.5,
				popoverClass: 'aaremo-tour-popover',
				disableActiveInteraction: true,
				// Clicking the dark overlay must NOT silently end the tour —
				// only an explicit click on ✕ / "Tour überspringen" / ESC does.
				overlayClickBehavior: () => {
					/* no-op */
				},
				onPopoverRender: (popover, opts) => {
					const idx = opts.driver.getActiveIndex() ?? 0
					const isFirst = idx === 0
					const isLast = idx === steps.length - 1

					// Inject an empty slot DIV; TourFooter teleports the real
					// <ButtonIcon> components into it. Driver recycles the
					// wrapper between steps, so we look up before creating.
					let slot = popover.wrapper.querySelector<HTMLElement>(
						'.aaremo-tour-footer-slot'
					)
					if (!slot) {
						slot = document.createElement('div')
						slot.className = 'aaremo-tour-footer-slot'
						const desc = popover.description
						if (desc?.parentNode) {
							desc.parentNode.insertBefore(slot, desc.nextSibling)
						} else {
							popover.wrapper.appendChild(slot)
						}
					}

					tourStore.setStepContext({
						target: slot,
						isFirst,
						isLast,
						next: async () => {
							if (isLast) {
								didComplete = true
								opts.driver.destroy()
								return
							}
							// Advance to the next showable step. Steps whose
							// verifyOnShow guard fails after prepare (the building
							// steps when we never landed on building/:id) are
							// skipped instead of being rendered as a centered
							// popover on the wrong page.
							let target = idx + 1
							while (target < steps.length) {
								let showable = true
								try {
									showable = await runStepPrepare(steps[target])
								} catch (err) {
									// Never let a prepare failure swallow the
									// click — log + show the step anyway so the
									// user isn't stuck.
									console.warn('[tour] prepare next failed:', err)
								}
								if (showable) break
								target++
							}
							if (!opts.driver.isActive()) return
							if (target >= steps.length) {
								// Every remaining step was skipped — finish.
								didComplete = true
								opts.driver.destroy()
								return
							}
							opts.driver.moveTo(target)
						},
						back: async () => {
							if (isFirst) {
								opts.driver.destroy()
								return
							}
							// Walk backwards to the previous showable step
							// (buildingDetail re-navigates to building/:id, so
							// going back re-enters it correctly).
							let target = idx - 1
							while (target >= 0) {
								let showable = true
								try {
									showable = await runStepPrepare(steps[target])
								} catch (err) {
									console.warn('[tour] prepare back failed:', err)
								}
								if (showable) break
								target--
							}
							if (!opts.driver.isActive()) return
							if (target < 0) return
							opts.driver.moveTo(target)
						},
						cancel: () => opts.driver.destroy(),
						complete: () => {
							didComplete = true
							opts.driver.destroy()
						},
					})
				},
				onDeselected: () => {
					tourStore.setStepContext(null)
				},
				onCloseClick: (_el, _step, opts) => {
					opts.driver.destroy()
				},
				onDestroyed: () => {
					// stop({persist:false}) sets suppressPersist so a logout-
					// initiated teardown is NOT recorded as a user skip.
					if (!suppressPersist) {
						if (didComplete) {
							tourStore.markCompleted(userId)
						} else {
							tourStore.markSkipped(userId)
						}
					}
					suppressPersist = false
					tourStore.setActive(false)
					tourStore.setStepContext(null)
					currentDriver = null
				},
			})

			// Run prepare for step 0 before drive() so its anchor exists.
			await runStepPrepare(steps[0])
			if (cancelled() || !canRun()) {
				// stop() was called mid-prepare; tear down the driver we just
				// built before it ever shows.
				try {
					currentDriver?.destroy()
				} catch {
					/* ignore */
				}
				return
			}
			tourStore.setActive(true)
			currentDriver.drive()
		} finally {
			starting = false
		}
	}

	/**
	 * Tear the tour down. Pass `persist: false` (default) when the teardown
	 * is NOT user-initiated (logout, route switch) so it doesn't get
	 * recorded as a "skipped" verdict and suppress the next auto-start.
	 */
	function stop({ persist = false }: { persist?: boolean } = {}) {
		// Invalidate any in-flight start() — its post-await guards will bail.
		runId++
		suppressPersist = !persist
		if (currentDriver?.isActive()) {
			currentDriver.destroy()
		}
		currentDriver = null
		starting = false
		didComplete = false
		tourStore.setStepContext(null)
		tourStore.setActive(false)
	}

	async function restart() {
		stop()
		const userId = whoami.value?.id
		tourStore.reset(userId)
		await start({ force: true })
	}

	return {
		canRun,
		shouldAutoStart,
		start,
		stop,
		restart,
	}
}
